import {
  Button,
  Flex,
  Group,
  LoadingOverlay,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useMemo, useState } from "react";
import { useLanguage } from "../../../../stores/LanguageStore";
import { createSMV, getSMV } from "../../../../api/ModelsApi";
import { notifications } from "@mantine/notifications";
import useNavigationBlocker from "../../../../components/View/NavigationBlocker";
import BackButton from "../../../../components/View/BackButton";
import { FileUploader } from "react-drag-drop-files";
import { IconUpload } from "@tabler/icons-react";
import SmvEditor from "./SmvEditor";

const CreateModelSmv = ({ handleClose, onCreated, inModal }) => {
  const lang = useLanguage((s) => s.language);
  const [dirty, setDirty] = useState(false);

  // Voice file
  const [incorrectLength, setIncorrectLength] = useState(false);
  const [invalidFile, setInvalidFile] = useState(false);
  const [fileKey, setFileKey] = useState(false);
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);

  const handleGetFile = (file) => {
    setFile(file);
    setFileKey(!fileKey);
    setIncorrectLength(false);
    setInvalidFile(false);
    setDirty(true);
  };

  const filePickerStyles = useMemo(() => {
    if (incorrectLength || invalidFile) {
      return {
        border: "1px solid",
        boxShadow: "#ee000066 0 0 8px",
        borderColor: "#cc0000",
        borderRadius: 12,
        padding: 8,
        textAlign: "center",
        cursor: "pointer",
      };
    } else
      return {
        border: "1px dashed",
        borderColor: "#00000066",
        borderRadius: 12,
        padding: 8,
        textAlign: "center",
        cursor: "pointer",
      };
  }, [incorrectLength, invalidFile]);

  const [submitting, setSubmitting] = useState(false);
  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const fd = new FormData(e.target);
    fd.append("voice_file", file);

    return createSMV(fd)
      .then((response) => {
        let newId = response.data.model_id;
        setSubmitting(true);

        getSMV()
          .then((res) => {
            let newItemsList = res.data.models_list;
            let newItem = newItemsList.find((x) => x.id === newId);
            notifications.show({
              title: lang.models.edit_success,
              message: lang.models.edit_success_text,
              color: "tertiary.8",
            });
            setDirty(false);

            setTimeout(() => {
              if (onCreated) {
                onCreated(newItem);
              } else handleClose?.();
            }, 50);
          })
          .finally(() => {
            setSubmitting(false);
          });
      })

      .catch((err) => {
        setSubmitting(false);
        if (err.response.data.state_message === "voice_file_incorrect_length") {
          return setIncorrectLength(true);
        } else if (err.response.data.state_message === "invalid_voice_file") {
          return setInvalidFile(true);
        }
        return setInvalidFile(true);
      });
  };

  useNavigationBlocker(dirty);

  return (
    <>
      <LoadingOverlay
        visible={submitting}
        overlayProps={{ blur: 1 }}
        zIndex={1001}
      />

      <Stack component={"form"} onSubmit={onSubmit} id="model-form">
        <TextInput
          label={lang.model_emails.create.dialog.name}
          placeholder={lang.model_emails.create.dialog.name}
          name="name"
          w="50%"
          required
          data-autofocus
          autoFocus
          onChange={() => setDirty(true)}
          size="md"
        />

        <Stack>
          <FileUploader
            handleChange={handleGetFile}
            key={fileKey}
            name="file"
            types={["WAV"]}
            hoverTitle={lang.components.files.drop_here}
            onDraggingStateChange={(d) => {
              setDragging(d);
            }}
            required={!file}
          >
            <Stack
              align="center"
              gap={"xs"}
              h={"35vh"}
              justify="center"
              style={filePickerStyles}
            >
              <IconUpload size={48} />
              <Text style={{ opacity: dragging ? 0.1 : 1 }} size="lg">
                Glissez-déposez votre fichier .wav ici pour modifier le modèle
              </Text>
            <Text mb={4} sx={{ opacity: 0.5 }}>
              {lang.model_smv.smv_editor.dialog.length}
            </Text>
              {file ? (
                <Text fw={600} c={"tertiary.8"}>
                  {lang.files.create.dialog.selected}: {file?.name}
                </Text>
              ) : (
                <Text fw={600} c={"red"}>
                  {lang.files.create.dialog.no_file}
                </Text>
              )}
            </Stack>
          </FileUploader>
        </Stack>

        <Group justify="space-between" mt={"sm"}>
          <SmvEditor setFile={handleGetFile} />
          <Flex gap={"md"}>
            <BackButton
              onClick={handleClose}
              label={lang.global.back}
              size="md"
            />
            <Button type="submit" size="md">
              {lang.model_emails.create.dialog.submit_button}
            </Button>
          </Flex>
        </Group>
      </Stack>
    </>
  );
};

export default CreateModelSmv;
